import { AppBar, IconButton, Stack, SwipeableDrawer, styled } from '@ethings/ui-toolkit';
import { Toolbar, useTheme } from '@mui/material';
import { FC, PropsWithChildren, RefObject, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { UI_TEMPLATE_DRIVEN_SIDEBAR_FLAG } from '../../feature-flags';
import { useBoolFlag } from '../flags';
import { FlexBox } from '../flex-box';
import { Icon } from '../icons';

interface Props extends PropsWithChildren {
  drawerContent: JSX.Element;
  drawerOpen: boolean;
  drawerRef?: RefObject<HTMLDivElement | undefined>;
  drawerWidth?: number;
  onDrawerClose: () => void;
  onDrawerOpen: () => void;
  toolbar: JSX.Element;
}

const StyledSwipeableDrawer = styled(SwipeableDrawer)({
  display: 'flex',
});

const StyledCloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(1),
  right: theme.spacing(1),
  zIndex: 1,
  width: 44,
  height: 44,
  minWidth: 44,
  minHeight: 44,
}));

export const AppMobileLayout: FC<Props> = ({ children, drawerContent, drawerOpen, drawerRef, drawerWidth = '100%', onDrawerClose, onDrawerOpen, toolbar }) => {
  const theme = useTheme();
  const isUiTemplateDrivenSidebar = useBoolFlag(UI_TEMPLATE_DRIVEN_SIDEBAR_FLAG);
  const { t } = useTranslation(['common']);

  return (
    <Stack className="App-root mobile" data-testid="app-page" direction="column" flexGrow={1} width="100%">
      <AppBar position="static">{toolbar}</AppBar>
      <Stack direction="row" flexGrow={1} overflow="hidden">
        <StyledSwipeableDrawer
          data-testid="app-drawer"
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            ref: drawerRef,
            sx: {
              display: 'flex',
              width: drawerWidth,
              backgroundColor: theme.sidebar.background,
              color: theme.sidebar.contrastText,
            },
          }}
          onClick={onDrawerClose}
          onClose={onDrawerClose}
          onOpen={onDrawerOpen}
          open={drawerOpen}
        >
          <StyledCloseButton
            aria-label={t('common:common.action.close')}
            data-testid="sidebar-close-button"
            id="sidebar-close-button"
            onClick={(event) => {
              event.stopPropagation();
              onDrawerClose();
            }}
            sx={{
              backgroundColor: `var(--ethings-sidebar-close-background, ${theme.sidebar.close.background})`,
              color: `var(--ethings-sidebar-close-contrastText, ${theme.sidebar.close.contrastText})`,
              '&:hover': {
                backgroundColor: `var(--ethings-sidebar-close-hoverBackground, ${theme.sidebar.close.hoverBackground})`,
              },
            }}
          >
            <Icon icon="X" />
          </StyledCloseButton>
          {!isUiTemplateDrivenSidebar &&
            <Toolbar />
          }
          {drawerContent}
        </StyledSwipeableDrawer>
        <FlexBox data-testid="app-body-wrapper" sx={{ minWidth: 0, flexDirection: 'column' }} >
          {children}
        </FlexBox>
      </Stack>
    </Stack >
  );
};
